<template>
  <div>
    <v-menu
      v-model="menu"
      max-width="290px"
      min-width="auto"
      style="top: 228px"
      offset-y
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          outlined
          v-model="dateText"
          v-bind="attrs"
          :prepend-inner-icon="icons.mdiCalendar"
          v-on="on"
          :disabled="disabled"
        ></v-text-field>
      </template>
      <v-row>
        <v-col>
          <v-date-picker
            v-model="state.value"
            :active-picker.sync="activePicker"
            :range="isRange"
            :type="pickerType"
            v-bind="{
              ...(pickerMin ? { min: pickerMin } : {}),
              ...(pickerMax ? { max: pickerMax } : {}),
            }"
            locale="FR-fr"
            first-day-of-week="1"
            selected-items-text="Durée selectionnée"
            :disabled="disabled"
            @click:year="onYearSelect"
          ></v-date-picker>
        </v-col>
      </v-row>
    </v-menu>
  </div>
</template>

<script>
import isEqual from "lodash/isEqual";

import { mdiCalendar } from "@mdi/js";
import {
  computed,
  nextTick,
  onMounted,
  reactive,
  ref,
  set,
  watch,
} from "@vue/composition-api";

import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";

import { URL_PARAM_NAMES } from "@/utils/constants";
import {
  getDateStr,
  getMonthDateStr,
  getYearStr,
  monthRange,
  ONE_DAY_MICROTIME,
} from "@/utils/dates";

const COMPONENT_NAME = "DateFilter";

const TODAY = new Date();
const YESTERDAY = new Date(TODAY.getTime() - ONE_DAY_MICROTIME);
const TOMORROW = new Date(TODAY.getTime() + ONE_DAY_MICROTIME);

/**
 * Either call this component giving an `initialValue`
 *  and listening on `valueChanged` event,
 * Or call it specifying a `store`
 *  (and eventually `storeGetter`/`storeUpdater`)
 *
 * In case of `isRange` === true,
 *  `initialValue` (or storedValue) must be an array [startDate, endDate]
 * otherwise, it must be a string value.
 */
export default {
  name: COMPONENT_NAME,
  props: {
    periodType: {
      type: String,
      default: "",
      validator(value) {
        return ["past-and-today", "past-only", "future-only", ""].includes(
          value
        );
      },
    },
    initialValue: {
      type: String | Array,
      validator(value) {
        if (Array.isArray(value)) {
          return value.length === 2 && value[0] !== undefined;
        }
        return typeof value === "string";
      },
    },
    isRange: {
      type: Boolean,
      default: true,
    },
    range: {
      type: String | Number,
      default: "14",
      validator(value) {
        return parseInt(value, 10);
      },
    },
    min: {
      type: String,
      default: "",
    },
    max: {
      type: String,
      default: "",
    },
    format: {
      type: String,
      default: "date",
      validator(value) {
        return ["date", "month", "year"].includes(value);
      },
    },
    store: {
      type: String,
      validator(value) {
        /* List of stores having :
        - getters["<store>/<storeGetter>"]
        - dispatch("<store>/<storeUpdater>", payload)
        */
        return [
          "acquisition",
          "admin",
          "audience",
          "c2c",
          "finance",
          "gd",
          "general",
          "gtrends",
          "linkMessApps",
          "marmiton",
          "newsRadar",
          "premium",
          "regie",
          "rmra",
          "webPerf",
        ].includes(value);
      },
    },
    storeGetter: {
      type: String,
      default: "getDates",
    },
    storeUpdater: {
      type: String,
      default: "updateDates",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    yearsAgo: {
      type: Number,
      default: null,
    },
    maxRangeDays: {
      type: [String, Number],
      default: 0,
    },
    syncQueryString: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const { store } = useProxy();

    const { addQueryStringParam, synchronizeFilterWithQueryString } =
      useQueryString();

    const state = reactive({
      value: props.isRange ? [] : "",
    });
    const menu = ref(false);
    const activePicker = ref(null);

    // =>  METHODS
    const onYearSelect = (year) => {
      if (props.format === "year" && !props.isRange) {
        const yearString = year.toString();
        store.dispatch(`${props.store}/${props.storeUpdater}`, yearString);
        emit("change", yearString);
        updateQueryString(yearString);
        menu.value = false;
      }
    };

    const updateQueryString = async (value) => {
      if (!props.syncQueryString) return;
      await addQueryStringParam({
        param: URL_PARAM_NAMES[props.storeUpdater] || props.storeUpdater,
        value: value,
        is_multiple: props.isRange,
        // is_integer: false,
        // is_boolean: false,
      });
    };

    const initValue = () => {
      if (props.isRange) {
        initRangeDates();
      } else {
        initStartDate();
      }
    };

    const initRangeDates = () => {
      if (
        storedValue?.value?.length !== 2 ||
        Object.values(storedValue.value).some(
          (x) => x === undefined || x.split("-").some((num) => isNaN(num))
        )
      ) {
        if (props.periodType === "past-and-today") {
          if (dateMax.value !== "") {
            const endDate = new Date(dateMax.value);
            const startDate = new Date(
              endDate.getTime() - props.range * ONE_DAY_MICROTIME
            );
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          } else {
            const endDate = TODAY;
            const startDate = new Date(
              TODAY.getTime() - props.range * ONE_DAY_MICROTIME
            );
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          }
        } else if (props.periodType === "past-only") {
          if (dateMax.value !== "") {
            const endDate = new Date(dateMax.value);
            const startDate = new Date(
              endDate.getTime() - props.range * ONE_DAY_MICROTIME
            );
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          } else {
            const endDate = YESTERDAY;
            const startDate = new Date(
              YESTERDAY.getTime() - props.range * ONE_DAY_MICROTIME
            );
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          }
        } else if (props.periodType === "future-only") {
          if (dateMin.value !== "") {
            const startDate = new Date(dateMin.value);
            const endDate = new Date(
              startDate.getTime() + props.range * ONE_DAY_MICROTIME
            );
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          } else {
            const startDate = TOMORROW;
            const endDate = new Date(
              TOMORROW.getTime() + props.range * ONE_DAY_MICROTIME
            );
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          }
        } else {
          if (dateMin.value !== "" && dateMax.value !== "") {
            const startDate = new Date(dateMin.value);
            const endDate = new Date(dateMax.value);
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          } else if (dateMin.value !== "") {
            const startDate = new Date(dateMin.value);
            const endDate = new Date(
              startDate.getTime() + props.range * ONE_DAY_MICROTIME
            );
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          } else if (dateMax.value !== "") {
            const endDate = new Date(dateMax.value);
            const startDate = new Date(
              endDate.getTime() - props.range * ONE_DAY_MICROTIME
            );
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          } else {
            const endDate = TODAY;
            const startDate = new Date(
              TODAY.getTime() - props.range * ONE_DAY_MICROTIME
            );
            set(state, "value", [
              getDateString(startDate),
              getDateString(endDate),
            ]);
          }
        }
      } else if (!isEqual(state.value, storedValue.value)) {
        set(state, "value", storedValue.value);
      }
    };

    const initStartDate = () => {
      if (
        !storedValue?.value ||
        (props.format !== "year" && storedValue.value.length < 7) ||
        (props.format === "year" && storedValue.value.length < 4)
      ) {
        if (props.periodType === "past-only") {
          set(state, "value", dateMax.value);
        } else if (props.periodType === "future-only") {
          set(state, "value", dateMin.value);
        } else {
          set(state, "value", getDateString(TODAY));
        }
      } else if (!isEqual(state.value, storedValue.value)) {
        set(state, "value", storedValue.value);
      }
    };

    const getDateString = (_date) => {
      if (props.format === "month") {
        return getMonthDateStr(_date);
      }
      if (props.format === "year") {
        return getYearStr(_date);
      }
      return getDateStr(_date);
    };

    // =>  COMPUTED
    const pickerType = computed(() =>
      props.format === "year" ? "month" : props.format
    );
    const viaStore = computed(() => props.store !== undefined);

    const storedValue = computed(() => {
      const value = viaStore.value
        ? store.getters[`${props.store}/${props.storeGetter}`]
        : props.initialValue;
      if (value && value.length > 0) {
        if (props.isRange) {
          return value.map((_date) => {
            if (props.format === "month") {
              const [startDate, _] = monthRange(_date);
              return getDateString(new Date(startDate));
            }
            return getDateString(new Date(_date));
          });
        }

        if (props.format === "month") {
          const [startDate, _] = monthRange(value);
          return getDateString(new Date(startDate));
        }
        if (props.format === "year") {
          return getYearStr(new Date(value));
        }
        return getDateString(new Date(value));
      }

      return props.isRange ? [] : "";
    });

    const dateText = computed(() => {
      const _value = storedValue.value;

      if (props.isRange && Array.isArray(_value) && _value.length === 2) {
        return _value.join(" => ");
      }
      if (props.format === "year" && _value) {
        return _value.toString().substring(0, 4);
      }

      return _value || "";
    });

    const dateMin = computed(() => {
      if (props.min) {
        return props.min;
      }
      if (props.yearsAgo) {
        const today = new Date();
        const year = today.getFullYear() - props.yearsAgo;
        if (props.format === "year") {
          return year.toString();
        }
        return `${year}-01-01`;
      }
      if (props.periodType === "future-only") {
        return getDateString(TOMORROW);
      }

      return "";
    });

    const dateMax = computed(() => {
      if (props.max !== "") {
        return props.max;
      }
      if (props.periodType === "past-only") {
        return getDateString(YESTERDAY);
      }

      return "";
    });

    const maxRangeDays = computed(() => parseInt(props.maxRangeDays, 10) || 0);

    const addDays = (dateStr, n) =>
      getDateStr(new Date(new Date(dateStr).getTime() + n * ONE_DAY_MICROTIME));

    // While a range is being picked (one date chosen so far), restrict the
    // selectable window to ±maxRangeDays around that anchor so the second click
    // cannot produce a span wider than the API allows.
    const rangeAnchor = computed(() =>
      maxRangeDays.value > 0 &&
      props.isRange &&
      Array.isArray(state.value) &&
      state.value.length === 1
        ? state.value[0]
        : null
    );

    const pickerMin = computed(() => {
      let min = dateMin.value || "";
      if (rangeAnchor.value) {
        const lower = addDays(rangeAnchor.value, -maxRangeDays.value);
        if (!min || lower > min) {
          min = lower;
        }
      }
      return min;
    });

    const pickerMax = computed(() => {
      let max = dateMax.value || "";
      if (rangeAnchor.value) {
        const upper = addDays(rangeAnchor.value, maxRangeDays.value);
        if (!max || upper < max) {
          max = upper;
        }
      }
      return max;
    });

    // =>  WATCH
    watch(
      storedValue,
      (newStoredValue) => {
        const pickerValue = newStoredValue;
        if (!isEqual(state.value, pickerValue)) {
          state.value = pickerValue;
        }
      },
      { immediate: true }
    );

    watch(
      () => state.value,
      async (newValue) => {
        if (props.format === "year") {
          return;
        }
        if (props.isRange && newValue.length === 2) {
          let [d1, d2] = newValue;

          if (d1 > d2) {
            [d1, d2] = [d2, d1];
          }

          if (maxRangeDays.value > 0) {
            const span = Math.round(
              (new Date(d2).getTime() - new Date(d1).getTime()) /
                ONE_DAY_MICROTIME
            );
            if (span > maxRangeDays.value) {
              d1 = addDays(d2, -maxRangeDays.value);
            }
          }

          newValue = [d1, d2];
        }

        if (viaStore.value === false) {
          if (newValue !== props.initialValue) {
            await updateQueryString(newValue);
            emit("valueChanged", newValue);
          }

          return;
        }

        if (props.isRange) {
          if (newValue.length === 2 && newValue[0] !== undefined) {
            await store.dispatch(
              `${props.store}/${props.storeUpdater}`,
              newValue
            );
            await updateQueryString(newValue);
          }
        } else if (newValue !== undefined) {
          await store.dispatch(
            `${props.store}/${props.storeUpdater}`,
            newValue
          );
          await updateQueryString(newValue);
        }
      }
    );

    watch(menu, (isOpen) => {
      if (isOpen) {
        if (props.format === "year") {
          nextTick(() => {
            activePicker.value = "YEAR";
          });
        }
      } else {
        activePicker.value = null;
      }
    });

    // =>  HOOKS
    onMounted(async () => {
      if (!props.syncQueryString) {
        initValue();
        return;
      }
      const _value = await synchronizeFilterWithQueryString({
        dispatcher: viaStore.value
          ? `${props.store}/${props.storeUpdater}`
          : "",
        param: URL_PARAM_NAMES[props.storeUpdater] || props.storeUpdater,
        value: state.value,
        is_multiple: props.isRange,
        // is_integer: false,
        // is_boolean: false,
        // dependsOn: undefined,
      });
      if (_value && _value.length > 0) {
        set(state, "value", _value);

        return;
      }

      initValue();
    });

    // =>  DATA
    return {
      dateMin,
      dateMax,
      pickerMin,
      pickerMax,
      state,
      dateText,
      icons: {
        mdiCalendar,
      },
      menu,
      activePicker,
      onYearSelect,
      pickerType,
    };
  },
};
</script>

<style scoped>
[role="menu"] {
  top: 176px !important;
  right: 0px !important;
}
</style>
